/** "Gerar processo" a partir do caso: opções do que passa para o processo + o cadastro do processo já preenchido. */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { navigate } from "@/lib/router";
import { useLookups } from "@/lib/catalog";
import { Field, Modal, StageSelect } from "./kit";
import { toast } from "./feedback";
import { NewProcessModal } from "@/pages/ProcessPages";

export interface CaseToProcessOpts { activities: boolean; flows: boolean; contracts: boolean; opposing: boolean; caseStageId: string | null }

/** Passa o trabalho do caso para o processo recém-cadastrado (chamado depois do cadastro). */
export async function applyCaseToProcess(caseId: string, processId: string, o: CaseToProcessOpts): Promise<void> {
  const r = await api.post<{ activities: number; flows: number; contracts: number; opposing: number; contractsSkipped: number }>(`/casos/${caseId}/gerar-processo`, { processId, ...o });
  const parts = [
    r.activities ? `${r.activities} atividade(s)` : "", r.flows ? `${r.flows} fluxo(s)` : "", r.contracts ? "contrato" : "", r.opposing ? `${r.opposing} parte(s) contrária(s)` : "",
  ].filter(Boolean);
  toast.ok(parts.length ? `Processo gerado. Passaram para ele: ${parts.join(", ")}.` : "Processo gerado.");
  if (r.contractsSkipped) toast.ok("O contrato continua vinculado ao caso (o caso tem mais de um processo) — ajuste no financeiro se precisar.");
}

export function defaultCaseStage(stages: Array<{ id: string; scope: string; key: string; name: string; active: boolean }> | undefined): string | null {
  return stages?.find((s) => s.scope === "caso" && s.active && /ajuiz|protocol/i.test(`${s.key} ${s.name}`))?.id ?? null;
}

export function GenerateProcessModal({ kase, hasProcesses, onClose }: { kase: { id: string; number: number; title: string }; hasProcesses: boolean; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const { catalog } = useLookups();
  const first = !hasProcesses;
  const [o, setO] = useState<CaseToProcessOpts>({ activities: first, flows: first, contracts: first, opposing: true, caseStageId: first ? defaultCaseStage(catalog?.stages) : null });
  const [step, setStep] = useState<"opcoes" | "cadastro">("opcoes");
  const chk = (k: keyof Omit<CaseToProcessOpts, "caseStageId">, label: string, hint: string) => (
    <label className="row gap-8" style={{ alignItems: "flex-start" }}>
      <input type="checkbox" checked={o[k]} onChange={(e) => setO({ ...o, [k]: e.target.checked })} />
      <span><b>{label}</b><br /><span className="muted small">{hint}</span></span>
    </label>
  );
  if (step === "cadastro") {
    return <NewProcessModal caseRef={{ id: kase.id, title: kase.title }} onClose={onClose} onCreated={(pid) => void (async () => {
      try { await applyCaseToProcess(kase.id, pid, o); } catch (e) { toast.error(`Processo cadastrado, mas a transferência falhou: ${(e as Error).message}`); }
      await Promise.all([qc.invalidateQueries({ queryKey: ["caso"] }), qc.invalidateQueries({ queryKey: ["atividades"] }), qc.invalidateQueries({ queryKey: ["bpm-execucoes"] })]);
      onClose(); navigate(`/processos/${pid}`);
    })()} />;
  }
  return (
    <Modal title={`Gerar processo — caso #${kase.number}`} onClose={onClose} wide footer={<>
      <button className="btn" onClick={onClose}>Cancelar</button>
      <button className="btn primary" onClick={() => setStep("cadastro")}>Continuar para os dados do processo</button>
    </>}>
      <div className="stack gap-12">
        <div className="muted small">
          O processo nasce <b>dentro do caso</b>, com área, tipo de ação, responsável e clientes já preenchidos. O caso continua com todo o histórico
          (atendimentos, documentos, conversa, atividades concluídas) — o processo enxerga tudo isso pelo caso.
          {hasProcesses && <> Este caso <b>já tem processo</b>: por isso as opções de passar o trabalho vêm desmarcadas.</>}
        </div>
        <div className="fld-label">O que passa para o processo</div>
        {chk("activities", "Atividades abertas do caso", "Pendências que ainda não são de nenhum processo passam a ficar no processo (continuam aparecendo no caso).")}
        {chk("flows", "Fluxos em andamento", "Petição, recurso etc. iniciados no caso: o fluxo e as próximas tarefas passam para o processo.")}
        {chk("contracts", "Contrato / honorários do caso", "O vínculo do contrato passa a incluir o processo (êxito e repasses já ficam nele). Só quando é o primeiro processo do caso.")}
        {chk("opposing", "Parte contrária do caso", "Entra como parte contrária do processo.")}
        <Field label="Etapa do caso depois do ajuizamento" hint="Opcional. Ex.: “Ajuizado”.">
          <StageSelect scope="caso" value={o.caseStageId} onChange={(v) => setO({ ...o, caseStageId: v })} emptyLabel="Não mudar a etapa" />
        </Field>
      </div>
    </Modal>
  );
}
